/**
 * Notificación nativa del navegador.
 *
 * Se usa como red de seguridad UX cuando un proceso largo (p. ej. la
 * planificación con IA, que puede durar de 1 minuto a 12+ horas) termina
 * mientras el usuario tiene la pestaña en background. Los toasts internos
 * solo se ven si la pestaña está visible; las notificaciones nativas
 * llegan al sistema operativo aunque el navegador esté minimizado.
 */

const SUPPORTED =
	typeof window !== "undefined" &&
	typeof Notification !== "undefined" &&
	typeof navigator !== "undefined";

function isSupported() {
	return SUPPORTED;
}

function getPermission() {
	if (!SUPPORTED) return "denied";
	return Notification.permission;
}

/**
 * Pide permiso de notificaciones si todavía no se ha pedido.
 * No fuerza el prompt si el usuario ya lo denegó.
 *
 * @returns {Promise<NotificationPermission>}
 */
async function requestPermission() {
	if (!SUPPORTED) return "denied";
	if (Notification.permission === "granted") return "granted";
	if (Notification.permission === "denied") return "denied";
	try {
		return await Notification.requestPermission();
	} catch (e) {
		console.warn("[notifications] requestPermission error", e);
		return "denied";
	}
}

/**
 * Muestra una notificación. Solo dispara si:
 *   - El navegador soporta Notification API.
 *   - El usuario ya concedió permiso.
 *   - La pestaña NO está visible (si está visible ya estamos mostrando un toast).
 *
 * @param {Object} opts
 * @param {string} opts.title
 * @param {string} [opts.body]
 * @param {string} [opts.tag] — sustituye notificaciones previas con el mismo tag.
 * @param {string} [opts.icon]
 * @param {() => void} [opts.onClick] — callback al hacer click; tras ejecutarlo, foca la ventana.
 * @returns {Notification|null}
 */
function notify({ title, body, tag, icon, onClick } = {}) {
	if (!SUPPORTED) return null;
	if (Notification.permission !== "granted") return null;
	// Si la pestaña está visible, los toasts internos cumplen su función.
	// Evitamos duplicar notificaciones.
	if (typeof document !== "undefined" && document.visibilityState === "visible") {
		return null;
	}

	try {
		const n = new Notification(title, {
			body: body || "",
			tag: tag || undefined,
			icon: icon || "/favicon.ico",
			renotify: !!tag,
		});

		n.onclick = () => {
			try {
				if (typeof window !== "undefined") {
					window.focus();
				}
				if (typeof onClick === "function") onClick();
			} finally {
				n.close();
			}
		};

		return n;
	} catch (e) {
		console.warn("[notifications] notify error", e);
		return null;
	}
}

export const browserNotificationService = {
	isSupported,
	getPermission,
	requestPermission,
	notify,
};
